import { computed, ref, provide } from 'vue'
import { useRoute } from 'vue-router'

import { deserializeFilter } from '@shared/utils/url'

export function useGridState(applyGridStateFromStorage = false) {
  const route = useRoute()
  const filterModel = ref({})
  const quickFilterValue = ref('')
  const selectedRows = ref([])

  const storageKey = computed(() => {
    if (!applyGridStateFromStorage) return undefined
    return `grid-state-${route.name}`
  })

  const hasRouteFilter = computed(() => !!route.query?.filter)
  const hasStorageFilter = computed(() => !!storageKey.value && applyGridStateFromStorage)

  const updateSelectedRows = (gridApi) => {
    if (!gridApi) {
      selectedRows.value = []
      return
    }

    const api = gridApi.api ?? gridApi

    const selectionState = api.getServerSideSelectionState()
    const isAllSelected = selectionState.selectAll

    if (isAllSelected) selectedRows.value = []
    else selectedRows.value = selectionState.toggledNodes
  }

  const setFilterModel = (gridApi, newModel) => {
    if (!gridApi) return
    filterModel.value = newModel

    gridApi.setFilterModel(newModel)
    gridApi.onFilterChanged()
  }

  const setQuickFilter = (gridApi, value, rowModelType) => {
    if (!gridApi) return
    quickFilterValue.value = value

    if (rowModelType === 'clientSide') {
      gridApi.setGridOption('quickFilterText', value)
    } else if (rowModelType === 'serverSide') {
      gridApi.onFilterChanged()
    }
  }

  const getCurrentGridState = (gridApi) => {
    if (!gridApi) return {}

    return {
      agGrid: {
        filterModel: gridApi.getFilterModel(),
        columnState: gridApi.getColumnState(),
      },
      quickFilterValue: quickFilterValue.value,
    }
  }

  const saveCurrentGridState = (gridApi, args = {}) => {
    if (!gridApi || !applyGridStateFromStorage || hasRouteFilter.value || !storageKey.value) return

    const gridRequestState = {
      agGrid: {
        filterModel: gridApi.getFilterModel(),
        columnState: gridApi.getColumnState(),
      },
      quickFilterValue: quickFilterValue.value,
      presetId: args?.presetId || null,
    }

    try {
      window.sessionStorage.setItem(storageKey.value, JSON.stringify(gridRequestState))
    } catch (error) {
      console.warn('[GridState] Failed to save grid state to sessionStorage:', error)
    }
  }

  const saveCurrentPageState = (pageNumber) => {
    if (storageKey.value && applyGridStateFromStorage) {
      try {
        window.sessionStorage.setItem(`${storageKey.value}-page`, pageNumber)
      } catch (error) {
        console.warn('[GridState] Failed to save current page state to sessionStorage:', error)
      }
    }
  }

  const loadCurrentPageState = () => {
    if (!storageKey.value) return null

    try {
      return window.sessionStorage.getItem(`${storageKey.value}-page`)
    } catch (error) {
      console.warn('[GridState] Failed to load current page state from sessionStorage:', error)
      return null
    }
  }

  const loadGridStateFromStorage = () => {
    if (!storageKey.value) return null

    try {
      const storedState = window.sessionStorage.getItem(storageKey.value)
      if (!storedState) return null

      return JSON.parse(storedState)
    } catch (error) {
      console.warn('[GridState] Failed to load grid state from sessionStorage:', error)
      return null
    }
  }

  const clearGridStateFromStorage = () => {
    if (!storageKey.value) return

    try {
      window.sessionStorage.removeItem(storageKey.value)
    } catch (error) {
      console.warn('[GridState] Failed to clear grid state from sessionStorage:', error)
    }
  }

  const syncFilterModel = (gridApi) => {
    if (!gridApi) return

    setFilterModel(gridApi, gridApi.getFilterModel())
  }

  const setURLorStorageFilterModel = (gridApi) => {
    if (!gridApi) return

    if (hasRouteFilter.value) setUrlFilterModel(gridApi)
    else if (hasStorageFilter.value) setStorageFilterModel(gridApi)
  }

  const setUrlFilterModel = (gridApi) => {
    if (!gridApi) return
    if (hasRouteFilter.value) setFilterModel(gridApi, deserializeFilter(route.query.filter))
  }

  const setStorageFilterModel = (gridApi) => {
    if (!gridApi) return

    const storedState = loadGridStateFromStorage()
    if (storedState) {
      try {
        if (storedState.agGrid?.filterModel) {
          gridApi.setFilterModel(storedState.agGrid.filterModel)
        }

        if (storedState.agGrid?.columnState) {
          gridApi.applyColumnState({ state: storedState.agGrid.columnState, applyOrder: true })
        }

        if (storedState.quickFilterValue) {
          quickFilterValue.value = storedState.quickFilterValue
          gridApi.setGridOption('quickFilterText', storedState.quickFilterValue)
        }
      } catch (error) {
        console.warn('[GridState] Failed to restore grid state:', error)
      }
    }
  }

  provide('gridState', {
    filterModel,
    quickFilterValue,
    setFilterModel,
    setQuickFilter,
    getCurrentGridState,
    saveCurrentGridState,
    loadGridStateFromStorage,
    clearGridStateFromStorage,
    selectedRows,
    setUrlFilterModel,
    setStorageFilterModel,
    setURLorStorageFilterModel,
    syncFilterModel,
    updateSelectedRows,
    saveCurrentPageState,
    loadCurrentPageState,
  })

  return {
    filterModel,
    quickFilterValue,
    setFilterModel,
    setQuickFilter,
    getCurrentGridState,
    saveCurrentGridState,
    loadGridStateFromStorage,
    clearGridStateFromStorage,
    selectedRows,
    setUrlFilterModel,
    setStorageFilterModel,
    setURLorStorageFilterModel,
    syncFilterModel,
    updateSelectedRows,
    saveCurrentPageState,
    loadCurrentPageState,
  }
}
